import type { ReactNode } from 'react';
import { useI18n } from '../i18n';
import { useGuessGame } from '../lib/useGuessGame';
import { localName, distanceKm, bearing, flagSrc, type Country } from '../lib/countries';
import { distanceColor } from '../lib/clue';
import GuessInput from '../components/GuessInput';
import Legend from '../components/Legend';
import EndBanner from '../components/EndBanner';
import { IconArrowUp } from '../components/Icons';
import CountryLink from '../components/CountryLink';

// Flag / Outline 共用:看谜面(国旗或轮廓)猜国家,线索给距离 + 方向 + 大洲。
export default function DistanceGame({
  mode,
  prompt,
  pool,
  hideFlags = false,
  endless = false,
  renderPuzzle,
}: {
  mode: string;
  prompt: string;
  pool?: Country[];
  hideFlags?: boolean;
  endless?: boolean;
  renderPuzzle: (answer: Country) => ReactNode;
}) {
  const { t, lang } = useI18n();
  const g = useGuessGame(mode, { pool, endless });
  const done = g.status !== 'playing';

  return (
    <div>
      <div className="puzzle">
        {renderPuzzle(g.answer)}
        <p className="puzzle-prompt">{prompt}</p>
      </div>

      {!done && (
        <>
          <GuessInput onGuess={g.guess} hideFlags={hideFlags} />
          {endless && (
            <div className="skip-row">
              <button onClick={g.skip}>{t.skip}</button>
            </div>
          )}
        </>
      )}

      {g.guesses.map((c, i) => {
        const isHit = c.cca3 === g.answer.cca3;
        const km = distanceKm(c, g.answer);
        const brg = bearing(c, g.answer);
        const region = c.region === g.answer.region;
        const last = i === g.guesses.length - 1;
        return (
          <div className={`dist-row${last ? ' reveal' : ''}`} key={c.cca3}>
            <img className="flag" src={flagSrc(c)} alt="" loading="lazy" />
            <CountryLink country={c} lang={lang} className="cell name country-name-link">
              <span>{localName(c, lang)}</span>
            </CountryLink>
            <div className={`cell ${region ? 'hit' : 'miss'}`}>
              <span>{c.region || '—'}</span>
            </div>
            <div
              className={isHit ? 'cell hit' : 'cell dist'}
              style={isHit ? undefined : { backgroundColor: distanceColor(km) }}
            >
              <span className="km">{km.toLocaleString(lang)} km</span>
              {!isHit && (
                <span className="dir" style={{ transform: `rotate(${brg}deg)` }}>
                  <IconArrowUp />
                </span>
              )}
            </div>
          </div>
        );
      })}

      {g.guesses.length > 0 && <Legend />}

      {g.guesses.length === 0 && g.status === 'playing' && (
        <p className="empty-hint">{t.emptyHint}</p>
      )}

      {done && <EndBanner mode={mode} g={g} />}
    </div>
  );
}
